// perfLogger — the request/report timing console (contract 03 §3; originally a 1:1 TS port of the
// v3 `performanceLogger`). Framework-agnostic.
//
// ORP-22839 — THE `false &&` HARD-DISABLE IS GONE, replaced by a RUNTIME switch.
// The port shipped with `enabled = false && …`, which made the logger unreachable in every build:
// answering "why is a report slow?" meant patching a source file and rebuilding. The flag is now
// resolved ONCE, lazily, from two sources (either turns it on):
//
//   • `localStorage['orphex:perf']` — no rebuild, works on staging, per-browser.
//   • `VITE_PERF_LOG=true` — declared + validated in `shared/config/env.ts`. Absent in production.
//
// The zero-cost property the `false &&` bought is preserved: `isOn()` is one cached boolean read,
// every public method early-returns on it, and NOTHING else in this module runs when off. The
// resolution itself is deferred out of module scope on purpose — `useEnv()` throws before boot's
// `validateEnv()`, and the old flag was only safe at module scope because `false &&` short-circuited
// past it.
//
// The DEV/localhost restriction the v3 flag carried is deliberately NOT kept: the reports this
// exists to measure are slow against staging, which is neither.
//
// The env read goes through its sole owner `useEnv()` rather than a raw Vite env read — the
// env-ownership guardrail (contract 01 §5) bans the raw env global outside shared/config/env.ts.
//
// Console `%c` styling lives in THIS FILE ONLY: `scripts/check-design-token-ownership.mjs` exempts
// exactly this path (`CONSOLE_STYLE_SOURCES`, an exact-path Set) from the raw-colour gate. Sibling
// modules must stay colour-free — see `perfMarks.ts`.

import { useEnv } from '@/shared/config'

export interface RequestLog {
  method: string
  url: string
  duration: number
  status: number | null
  timestamp: number
  /** Response payload size in bytes, when the transport could determine one. */
  bytes?: number | undefined
}

/** The per-report phase timings `perfMarks.endReportRun()` hands over for rendering. */
export interface ReportPhases {
  transformMs: number | null
  firstRowMs: number | null
  chartMs: number | null
  totalMs: number
}

const SESSION_KEY = 'perf_session'

/** localStorage switch. Any value except an explicit off-value turns the logger on. */
export const PERF_FLAG_KEY = 'orphex:perf'
const OFF_VALUES = new Set(['', '0', 'false', 'off'])

const HEADING = 'color:#3498db;font-weight:bold'
const SLOW = 'color:#e74c3c;font-weight:bold'
const WARN = 'color:#f39c12'
const OK = 'color:#2ecc71'

function fromStorage(): boolean {
  try {
    const value = globalThis.localStorage?.getItem(PERF_FLAG_KEY)
    return value !== null && value !== undefined && !OFF_VALUES.has(value)
  } catch {
    // Storage unavailable (Safari private mode, blocked cookies) is "not switched on", never a crash.
    return false
  }
}

function fromEnv(): boolean {
  try {
    return useEnv().PERF_LOG
  } catch {
    // Called before validateEnv() — treat as off rather than crashing an early importer.
    return false
  }
}

/** Resolve the switch. Called at most once per page load; the result is cached on the instance. */
function resolveEnabled(): boolean {
  if (typeof globalThis.performance === 'undefined') return false
  return fromStorage() || fromEnv()
}

function durationStyle(ms: number): string {
  if (ms > 1000) return SLOW
  if (ms > 500) return WARN
  return OK
}

function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function formatMs(ms: number | null): string {
  return ms === null ? 'n/a' : `${Math.round(ms)}ms`
}

class PerformanceLogger {
  private logs: RequestLog[] = []
  private sessionLabel: string | null = null
  /** `null` until first use — the lazily resolved runtime switch (see the header). */
  private enabled: boolean | null = null

  /** One cached boolean read. This is the whole cost of the logger while it is off. */
  private isOn(): boolean {
    this.enabled ??= resolveEnabled()
    return this.enabled
  }

  /** Public switch probe, so callers can skip building payloads the logger would discard. */
  isEnabled(): boolean {
    return this.isOn()
  }

  startSession(label: string): void {
    if (!this.isOn()) return

    this.logs = []
    this.sessionLabel = label
    performance.mark(`${SESSION_KEY}:${label}:start`)
    console.group(`[Perf] Session started: ${label}`)
  }

  logRequest(entry: RequestLog): void {
    if (!this.isOn()) return

    this.logs.push(entry)

    console.log(
      `%c[Perf] ${entry.method.toUpperCase()} ${entry.url} → ${entry.status} in ${entry.duration}ms (${formatBytes(entry.bytes)})`,
      durationStyle(entry.duration),
    )
  }

  endSession(): void {
    if (!this.isOn() || !this.sessionLabel) return

    performance.mark(`${SESSION_KEY}:${this.sessionLabel}:end`)

    const total = this.logs.reduce((sum, l) => sum + l.duration, 0)

    console.log(
      `%c[Perf] Session "${this.sessionLabel}" complete — ${this.logs.length} requests, ${total}ms total`,
      HEADING,
    )

    this.printRequestTable()
    console.groupEnd()
    this.sessionLabel = null
  }

  /**
   * Close a report run: the request table this run accumulated, plus the fetch → transform →
   * render breakdown. `perfMarks.endReportRun()` is the only caller.
   */
  endReportSession(phases: ReportPhases): void {
    if (!this.isOn() || !this.sessionLabel) return

    const label = this.sessionLabel
    const requestMs = this.logs.reduce((sum, l) => sum + l.duration, 0)
    const bytes = this.logs.reduce((sum, l) => sum + (l.bytes ?? 0), 0)

    console.log(
      `%c[Perf] Report "${label}" — ${this.logs.length} controller requests, ${formatBytes(bytes)}, ${formatMs(phases.totalMs)} total`,
      HEADING,
    )

    this.printRequestTable()

    console.table([
      // Sum, NOT wall time: concurrent sections push it above the elapsed time, and the
      // `processing:true` poll's 30s sleeps sit between requests where this sum cannot see them.
      { phase: 'requests (sum of durations)', value: `${requestMs}ms` },
      { phase: 'transform', value: formatMs(phases.transformMs) },
      { phase: 'time-to-first-row', value: formatMs(phases.firstRowMs) },
      { phase: 'time-to-chart', value: formatMs(phases.chartMs) },
      { phase: 'total', value: formatMs(phases.totalMs) },
    ])

    console.groupEnd()
    this.sessionLabel = null
  }

  private printRequestTable(): void {
    const sorted = [...this.logs].sort((a, b) => b.duration - a.duration)
    console.table(
      sorted.map((l, i) => ({
        '#': i + 1,
        method: l.method.toUpperCase(),
        url: l.url,
        status: l.status,
        duration: `${l.duration}ms`,
        size: formatBytes(l.bytes),
      })),
    )
  }

  getSummary() {
    return {
      label: this.sessionLabel,
      total: this.logs.reduce((s, l) => s + l.duration, 0),
      count: this.logs.length,
      slowest: [...this.logs].sort((a, b) => b.duration - a.duration)[0] ?? null,
      logs: [...this.logs],
    }
  }
}

export const perfLogger = new PerformanceLogger()
